// For production use with real backend
// Install: npm install socket.io-client

import React, { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import {
  Pizza,
  Users,
  Eye,
  EyeOff,
  RotateCcw,
  Copy,
  Check,
  Flame,
  Crown,
  Wifi,
  WifiOff,
} from "lucide-react";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

const PointingPizza = () => {
  const [roomId, setRoomId] = useState("");
  const [currentRoom, setCurrentRoom] = useState(null);
  const [userName, setUserName] = useState("");
  const [isJoined, setIsJoined] = useState(false);
  const [rooms, setRooms] = useState({});
  const [selectedValue, setSelectedValue] = useState(null);
  const [showCopied, setShowCopied] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);

  const fibonacciValues = [
    "0",
    "1",
    "2",
    "3",
    "5",
    "8",
    "13",
    "21",
    "34",
    "55",
    "89",
    "?",
    "☕",
  ];

  // Initialize Socket.IO connection
  useEffect(() => {
    const socket = io(BACKEND_URL);
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Connected to backend");
      setIsConnected(true);
    });

    socket.on("disconnect", () => {
      console.log("Disconnected from backend");
      setIsConnected(false);
    });

    // Listen for room updates
    socket.on("room_created", ({ roomId, room }) => {
      setRooms((prev) => ({ ...prev, [roomId]: room }));
    });

    socket.on("user_joined", ({ roomId, room }) => {
      setRooms((prev) => ({ ...prev, [roomId]: room }));
    });

    socket.on("vote_cast", ({ roomId, room }) => {
      setRooms((prev) => ({ ...prev, [roomId]: room }));
    });

    socket.on("votes_revealed", ({ roomId, room }) => {
      setRooms((prev) => ({ ...prev, [roomId]: room }));
    });

    socket.on("votes_reset", ({ roomId, room }) => {
      setRooms((prev) => ({ ...prev, [roomId]: room }));
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const generateRoomId = () => {
    const pizzaWords = [
      "pepperoni",
      "margherita",
      "hawaiian",
      "supreme",
      "veggie",
      "bbq",
      "buffalo",
      "alfredo",
    ];
    const word = pizzaWords[Math.floor(Math.random() * pizzaWords.length)];
    const num = Math.floor(Math.random() * 9000) + 1000;
    return `${word}-${num}`;
  };

  const createRoom = () => {
    if (!userName.trim()) {
      alert("Please enter your name first! 🍕");
      return;
    }
    const newRoomId = generateRoomId();
    const newRoom = {
      id: newRoomId,
      users: [
        {
          id: Date.now().toString(),
          name: userName,
          vote: null,
          isCreator: true,
        },
      ],
      revealed: false,
      createdAt: Date.now(),
    };

    setRooms((prev) => ({ ...prev, [newRoomId]: newRoom }));
    setCurrentRoom(newRoomId);
    setRoomId(newRoomId);
    setIsJoined(true);

    // Emit to backend
    socketRef.current?.emit("room_created", {
      roomId: newRoomId,
      room: newRoom,
    });
  };

  const joinRoom = () => {
    if (!userName.trim()) {
      alert("Please enter your name first! 🍕");
      return;
    }
    if (!roomId.trim()) {
      alert("Please enter a room ID! 🍕");
      return;
    }

    // Request room data from backend
    socketRef.current?.emit("get_room", roomId, (room) => {
      if (!room) {
        alert("Room not found! Check your room ID. 🍕");
        return;
      }

      const userExists = room.users.find((u) => u.name === userName);
      if (!userExists) {
        const updatedRoom = {
          ...room,
          users: [
            ...room.users,
            {
              id: Date.now().toString(),
              name: userName,
              vote: null,
              isCreator: false,
            },
          ],
        };
        setRooms((prev) => ({ ...prev, [roomId]: updatedRoom }));
        socketRef.current?.emit("user_joined", { roomId, room: updatedRoom });
      } else {
        setRooms((prev) => ({ ...prev, [roomId]: room }));
      }

      setCurrentRoom(roomId);
      setIsJoined(true);
    });
  };

  const castVote = (value) => {
    const room = rooms[currentRoom];
    const updatedUsers = room.users.map((u) =>
      u.name === userName ? { ...u, vote: value } : u
    );
    const updatedRoom = { ...room, users: updatedUsers };

    setRooms((prev) => ({
      ...prev,
      [currentRoom]: updatedRoom,
    }));
    setSelectedValue(value);

    socketRef.current?.emit("vote_cast", {
      roomId: currentRoom,
      room: updatedRoom,
    });
  };

  const revealVotes = () => {
    const room = rooms[currentRoom];
    const updatedRoom = { ...room, revealed: true };

    setRooms((prev) => ({
      ...prev,
      [currentRoom]: updatedRoom,
    }));

    socketRef.current?.emit("votes_revealed", {
      roomId: currentRoom,
      room: updatedRoom,
    });
  };

  const resetVotes = () => {
    const room = rooms[currentRoom];
    const updatedUsers = room.users.map((u) => ({ ...u, vote: null }));
    const updatedRoom = { ...room, users: updatedUsers, revealed: false };

    setRooms((prev) => ({
      ...prev,
      [currentRoom]: updatedRoom,
    }));
    setSelectedValue(null);

    socketRef.current?.emit("votes_reset", {
      roomId: currentRoom,
      room: updatedRoom,
    });
  };

  const leaveRoom = () => {
    setCurrentRoom(null);
    setIsJoined(false);
    setSelectedValue(null);
    setRoomId("");
  };

  const copyRoomId = () => {
    navigator.clipboard.writeText(currentRoom);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const currentRoomData = currentRoom ? rooms[currentRoom] : null;
  const currentUser = currentRoomData?.users.find((u) => u.name === userName);
  const isCreator = currentUser?.isCreator;

  const calculateStats = () => {
    if (!currentRoomData?.revealed) return null;
    const numericVotes = currentRoomData.users
      .map((u) => u.vote)
      .filter((v) => v && !isNaN(v))
      .map(Number);

    if (numericVotes.length === 0) return null;

    const avg = numericVotes.reduce((a, b) => a + b, 0) / numericVotes.length;
    const sorted = [...numericVotes].sort((a, b) => a - b);
    const median =
      sorted.length % 2 === 0
        ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : sorted[Math.floor(sorted.length / 2)];

    return {
      avg: avg.toFixed(1),
      median,
      min: Math.min(...numericVotes),
      max: Math.max(...numericVotes),
    };
  };

  const stats = calculateStats();

  if (!isJoined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 flex items-center justify-center p-4 font-sans">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Freckle+Face&family=Quicksand:wght@400;500;600;700&display=swap');
          
          body {
            font-family: 'Quicksand', sans-serif;
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-20px) rotate(5deg); }
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @keyframes pulse-glow {
            0%, 100% { box-shadow: 0 0 20px rgba(239, 68, 68, 0.4), 0 0 40px rgba(249, 115, 22, 0.2); }
            50% { box-shadow: 0 0 30px rgba(239, 68, 68, 0.6), 0 0 60px rgba(249, 115, 22, 0.4); }
          }

          .pizza-float {
            animation: float 3s ease-in-out infinite;
          }

          .title-font {
            font-family: 'Freckle Face', cursive;
            text-shadow: 3px 3px 0px rgba(239, 68, 68, 0.3);
          }

          .card-hover {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          .card-hover:hover {
            transform: translateY(-4px);
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          }

          .input-field {
            transition: all 0.3s ease;
          }

          .input-field:focus {
            transform: scale(1.02);
            box-shadow: 0 8px 20px rgba(249, 115, 22, 0.2);
          }

          .btn-primary {
            background: linear-gradient(135deg, #f97316 0%, #ef4444 100%);
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }

          .btn-primary::before {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.3);
            transform: translate(-50%, -50%);
            transition: width 0.6s, height 0.6s;
          }

          .btn-primary:hover::before {
            width: 300px;
            height: 300px;
          }

          .btn-primary:hover {
            transform: scale(1.05);
            box-shadow: 0 10px 30px rgba(239, 68, 68, 0.4);
          }

          .flame-accent {
            animation: pulse-glow 2s ease-in-out infinite;
          }
        `}</style>

        <div className="w-full max-w-md">
          {!isConnected && (
            <div className="mb-4 p-4 bg-red-100 border-3 border-red-300 rounded-xl text-red-700 text-center font-semibold">
              ⚠️ Connecting to server...
            </div>
          )}

          <div className="text-center mb-8">
            <div className="inline-block pizza-float mb-4">
              <div className="relative">
                <Pizza
                  className="w-24 h-24 text-orange-500"
                  strokeWidth={1.5}
                />
                <Flame className="w-8 h-8 text-red-500 absolute -top-2 -right-2 flame-accent" />
              </div>
            </div>
            <h1 className="text-6xl font-bold text-red-600 mb-3 title-font">
              Pointing Pizza
            </h1>
            <p className="text-gray-700 text-lg font-medium">
              Estimate like a chef, vote like a team! 🍕
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 card-hover border-4 border-orange-200">
            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-2 text-lg">
                Your Name
              </label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Enter your name..."
                className="w-full px-4 py-3 border-3 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400 text-lg input-field"
                onKeyPress={(e) => e.key === "Enter" && createRoom()}
              />
            </div>

            <button
              onClick={createRoom}
              disabled={!isConnected}
              className="w-full py-4 rounded-xl text-white font-bold text-lg mb-4 btn-primary relative z-10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              🍕 Create New Room
            </button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-orange-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500 font-semibold">
                  OR
                </span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-gray-700 font-semibold mb-2 text-lg">
                Room ID
              </label>
              <input
                type="text"
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                placeholder="pepperoni-1234..."
                className="w-full px-4 py-3 border-3 border-orange-200 rounded-xl focus:outline-none focus:border-orange-400 text-lg input-field"
                onKeyPress={(e) => e.key === "Enter" && joinRoom()}
              />
            </div>

            <button
              onClick={joinRoom}
              disabled={!isConnected}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-yellow-400 to-orange-400 text-white font-bold text-lg hover:from-yellow-500 hover:to-orange-500 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              🎯 Join Room
            </button>
          </div>

          <div className="text-center mt-6 text-gray-600">
            <p className="text-sm">
              Perfect for agile teams & sprint planning! 🚀
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-yellow-50 p-4 font-sans">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Freckle+Face&family=Quicksand:wght@400;500;600;700&display=swap');
        
        body {
          font-family: 'Quicksand', sans-serif;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }

        @keyframes cardFlip {
          0% { transform: rotateY(0deg) scale(1); }
          50% { transform: rotateY(180deg) scale(1.1); }
          100% { transform: rotateY(360deg) scale(1); }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .pizza-float {
          animation: float 3s ease-in-out infinite;
        }

        .title-font {
          font-family: 'Freckle Face', cursive;
          text-shadow: 2px 2px 0px rgba(239, 68, 68, 0.3);
        }

        .card-flip {
          animation: cardFlip 0.6s ease-in-out;
        }

        .slide-in {
          animation: slideInUp 0.4s ease-out;
        }

        .scale-in {
          animation: scaleIn 0.3s ease-out;
        }

        .vote-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
        }

        .vote-card:hover {
          transform: translateY(-8px) scale(1.05);
          box-shadow: 0 15px 35px rgba(249, 115, 22, 0.4);
        }

        .vote-card.selected {
          transform: translateY(-12px) scale(1.1);
          box-shadow: 0 20px 40px rgba(239, 68, 68, 0.5);
        }

        .user-card {
          transition: all 0.3s ease;
        }

        .user-card:hover {
          transform: translateX(4px);
        }

        .btn-action {
          transition: all 0.3s ease;
        }

        .btn-action:hover {
          transform: scale(1.05);
        }

        .stats-badge {
          animation: scaleIn 0.5s ease-out;
        }
      `}</style>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-xl p-6 mb-6 border-4 border-orange-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Pizza
                className="w-12 h-12 text-orange-500 pizza-float"
                strokeWidth={1.5}
              />
              <div>
                <h1 className="text-3xl font-bold text-red-600 title-font">
                  Pointing Pizza
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-gray-600 font-semibold">Room:</span>
                  <code className="bg-orange-100 px-3 py-1 rounded-lg text-orange-700 font-bold">
                    {currentRoom}
                  </code>
                  <button
                    onClick={copyRoomId}
                    className="p-2 hover:bg-orange-100 rounded-lg transition-colors btn-action"
                  >
                    {showCopied ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <Copy className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                  isConnected
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {isConnected ? (
                  <>
                    <Wifi className="w-4 h-4" />
                    <span className="text-sm font-semibold">Connected</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="w-4 h-4" />
                    <span className="text-sm font-semibold">Offline</span>
                  </>
                )}
              </div>
              {isCreator && (
                <>
                  <button
                    onClick={
                      currentRoomData?.revealed ? resetVotes : revealVotes
                    }
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-bold flex items-center gap-2 hover:from-blue-600 hover:to-purple-600 btn-action"
                  >
                    {currentRoomData?.revealed ? (
                      <>
                        <RotateCcw className="w-5 h-5" />
                        Reset
                      </>
                    ) : (
                      <>
                        <Eye className="w-5 h-5" />
                        Reveal
                      </>
                    )}
                  </button>
                </>
              )}
              <button
                onClick={leaveRoom}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 btn-action"
              >
                Leave
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Voting Cards */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl shadow-xl p-8 border-4 border-orange-200">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <span>Pick Your Slice</span>
                <Pizza className="w-6 h-6 text-orange-500" />
              </h2>

              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-7 gap-4">
                {fibonacciValues.map((value, index) => (
                  <button
                    key={value}
                    onClick={() => castVote(value)}
                    disabled={currentRoomData?.revealed}
                    className={`vote-card aspect-square rounded-2xl font-bold text-2xl flex items-center justify-center border-4 scale-in ${
                      selectedValue === value
                        ? "bg-gradient-to-br from-orange-400 to-red-500 text-white border-red-600 selected"
                        : "bg-gradient-to-br from-orange-50 to-yellow-50 text-orange-700 border-orange-300 hover:border-orange-400"
                    } ${
                      currentRoomData?.revealed
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    {value}
                  </button>
                ))}
              </div>

              {currentUser?.vote && !currentRoomData?.revealed && (
                <div className="mt-6 p-4 bg-green-50 border-3 border-green-300 rounded-2xl slide-in">
                  <p className="text-green-700 font-semibold text-center">
                    ✓ You voted:{" "}
                    <span className="text-2xl font-bold">
                      {currentUser.vote}
                    </span>
                  </p>
                </div>
              )}

              {currentRoomData?.revealed && stats && (
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-2xl border-3 border-blue-300 stats-badge">
                    <div className="text-blue-600 text-sm font-semibold mb-1">
                      Average
                    </div>
                    <div className="text-3xl font-bold text-blue-700">
                      {stats.avg}
                    </div>
                  </div>
                  <div
                    className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-2xl border-3 border-purple-300 stats-badge"
                    style={{ animationDelay: "0.1s" }}
                  >
                    <div className="text-purple-600 text-sm font-semibold mb-1">
                      Median
                    </div>
                    <div className="text-3xl font-bold text-purple-700">
                      {stats.median}
                    </div>
                  </div>
                  <div
                    className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-2xl border-3 border-green-300 stats-badge"
                    style={{ animationDelay: "0.2s" }}
                  >
                    <div className="text-green-600 text-sm font-semibold mb-1">
                      Min
                    </div>
                    <div className="text-3xl font-bold text-green-700">
                      {stats.min}
                    </div>
                  </div>
                  <div
                    className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-2xl border-3 border-red-300 stats-badge"
                    style={{ animationDelay: "0.3s" }}
                  >
                    <div className="text-red-600 text-sm font-semibold mb-1">
                      Max
                    </div>
                    <div className="text-3xl font-bold text-red-700">
                      {stats.max}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Users Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl shadow-xl p-6 border-4 border-orange-200">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Users className="w-6 h-6 text-orange-500" />
                Team ({currentRoomData?.users.length})
              </h2>

              <div className="space-y-3">
                {currentRoomData?.users.map((user, index) => (
                  <div
                    key={user.id}
                    className="user-card bg-gradient-to-r from-orange-50 to-yellow-50 border-3 border-orange-200 rounded-xl p-4 slide-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            user.vote ? "bg-green-500" : "bg-gray-300"
                          }`}
                        />
                        <span className="font-bold text-gray-800 flex items-center gap-2">
                          {user.name}
                          {user.isCreator && (
                            <Crown className="w-4 h-4 text-yellow-500" />
                          )}
                        </span>
                      </div>

                      <div>
                        {currentRoomData.revealed && user.vote ? (
                          <div className="bg-gradient-to-br from-orange-400 to-red-500 text-white px-4 py-2 rounded-lg font-bold text-xl border-2 border-red-600 card-flip">
                            {user.vote}
                          </div>
                        ) : user.vote ? (
                          <div className="bg-gradient-to-br from-orange-300 to-red-400 px-4 py-2 rounded-lg border-2 border-orange-400">
                            <Pizza className="w-5 h-5 text-white" />
                          </div>
                        ) : (
                          <div className="bg-gray-200 px-4 py-2 rounded-lg border-2 border-gray-300">
                            <EyeOff className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {!currentRoomData?.revealed && (
                <div className="mt-6 p-4 bg-yellow-50 border-3 border-yellow-300 rounded-xl">
                  <p className="text-yellow-700 text-sm text-center font-semibold">
                    🔒 Votes hidden until reveal
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointingPizza;
